import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/components/layout/PageHeader';
import { Icon } from '@/components/ui/Icon';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Modal } from '@/components/ui/Modal';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { toast } from '@/utils/toast';
import { useAuth } from '@/hooks/useAuth';
import { useTimezone } from '@/contexts/TimezoneContext';
import { statusService } from '@/services/status.service';
import {
  SystemStatusLevel,
  FeatureRequestType,
  FeatureRequestStatus,
  ChangelogEntry,
  FeatureRequest,
} from '@/types/status';

/**
 * StatusContent
 *
 * Shared content for the System Status + Changelog + Feature Request page.
 * Fetches system status and changelog via PUBLIC endpoints (no auth required),
 * and gates the feature-request form + "your requests" list on `isAuthenticated`.
 *
 * Rendered by:
 * - `pages/Status.tsx` — the public, chrome-wrapped standalone page at `/status`
 *   (no login, no app sidebar — mirrors the `/design-system` pattern).
 *
 * Kept as its own component (rather than inline in the page) so the data
 * fetching/rendering logic isn't duplicated if it's ever also surfaced
 * inside the authenticated app shell.
 */

export const StatusContent = () => {
  const { t } = useTranslation('common');
  const { isAuthenticated } = useAuth();
  const { formatDateTime, formatDateOnly } = useTimezone();
  const queryClient = useQueryClient();

  // Status level visual config (moved inside component to access t())
  const STATUS_CONFIG: Record<
    SystemStatusLevel,
    { icon: string; color: string; bgColor: string; label: string }
  > = {
    operational: {
      icon: ICONS.CHECK_CIRCLE,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      label: t('status_operational'),
    },
    degraded: {
      icon: ICONS.WARNING,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      label: t('status_degraded'),
    },
    down: {
      icon: ICONS.CROSS_CIRCLE,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      label: t('status_down'),
    },
    maintenance: {
      icon: ICONS.TOOLS,
      color: 'text-dark-teal',
      bgColor: 'bg-soft-mint',
      label: t('status_maintenance'),
    },
  };

  // Feature request type options
  const REQUEST_TYPE_OPTIONS = [
    { value: 'feature', label: t('feedback_type_feature'), icon: ICONS.BULB },
    { value: 'bug', label: t('feedback_type_bug'), icon: ICONS.WARNING },
    { value: 'improvement', label: t('feedback_type_improvement'), icon: ICONS.SPARK },
    { value: 'other', label: t('feedback_type_other'), icon: ICONS.CHAT },
  ];

  // Status badge config
  const STATUS_BADGE_CONFIG: Record<
    FeatureRequestStatus,
    { color: string; bgColor: string; label: string }
  > = {
    new: { color: 'text-gray-700', bgColor: 'bg-gray-100', label: t('feedback_status_new') },
    reviewed: { color: 'text-dark-teal', bgColor: 'bg-soft-mint', label: t('feedback_status_reviewed') },
    planned: { color: 'text-primary-700', bgColor: 'bg-primary-100', label: t('feedback_status_planned') },
    in_progress: { color: 'text-orange-700', bgColor: 'bg-orange-100', label: t('feedback_status_in_progress') },
    completed: { color: 'text-green-700', bgColor: 'bg-green-100', label: t('feedback_status_completed') },
    declined: { color: 'text-red-700', bgColor: 'bg-red-100', label: t('feedback_status_declined') },
  };

  // Changelog type badge config
  const CHANGELOG_TYPE_CONFIG: Record<
    string,
    { color: string; bgColor: string; label: string; dotColor: string }
  > = {
    feature: { color: 'text-green-700', bgColor: 'bg-green-100', label: t('changelog_type_feature'), dotColor: 'bg-emerald-500' },
    improvement: { color: 'text-primary-700', bgColor: 'bg-primary-50', label: t('changelog_type_improvement'), dotColor: 'bg-primary-600' },
    fix: { color: 'text-amber-700', bgColor: 'bg-amber-100', label: t('changelog_type_fix'), dotColor: 'bg-amber-500' },
    security: { color: 'text-red-700', bgColor: 'bg-red-100', label: t('changelog_type_security'), dotColor: 'bg-red-500' },
  };

  // Modal state
  const [selectedChangelog, setSelectedChangelog] = useState<ChangelogEntry | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<FeatureRequest | null>(null);

  // Changelog "reveal N at a time" state
  const CHANGELOG_STEP = 5;
  const [visibleCount, setVisibleCount] = useState(CHANGELOG_STEP);

  // Form state
  const [formData, setFormData] = useState({
    type: 'feature' as FeatureRequestType,
    title: '',
    description: '',
  });
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch system status (public)
  const { data: systemStatus, isLoading: statusLoading } = useQuery({
    queryKey: ['system-status'],
    queryFn: statusService.getSystemStatus,
    staleTime: 30 * 1000, // 30 seconds - status is real-time
    refetchInterval: 60 * 1000, // Refetch every minute
  });

  // Fetch changelog (public)
  const { data: changelog, isLoading: changelogLoading } = useQuery({
    queryKey: ['changelog'],
    queryFn: () => statusService.getChangelog(50),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch user's feature requests (requires auth)
  const { data: userRequests } = useQuery({
    queryKey: ['user-feature-requests'],
    queryFn: statusService.getUserFeatureRequests,
    enabled: isAuthenticated,
    staleTime: 60 * 1000, // 1 minute
  });

  // Submit feature request mutation
  const submitMutation = useMutation({
    mutationFn: statusService.submitFeatureRequest,
    onSuccess: () => {
      toast.success(t('feedback_success'));
      // Reset form
      setFormData({ type: 'feature', title: '', description: '' });
      setScreenshot(null);
      setErrors({});
      // Refetch user requests
      queryClient.invalidateQueries({ queryKey: ['user-feature-requests'] });
    },
    onError: () => {
      toast.error(t('feedback_error'));
    },
  });

  // Form validation
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > 200) {
      newErrors.title = 'Title must be 200 characters or less';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.length > 2000) {
      newErrors.description = 'Description must be 2000 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submit
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error(t('validation_fix_errors'));
      return;
    }

    submitMutation.mutate({
      ...formData,
      screenshot: screenshot || undefined,
    });
  };

  // Handle file upload
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(t('file_too_large'));
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error(t('file_invalid_type'));
        return;
      }

      setScreenshot(file);
    }
  };

  // Get overall status config
  const overallStatusConfig = systemStatus
    ? STATUS_CONFIG[systemStatus.overallStatus]
    : STATUS_CONFIG.operational;

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <PageHeader
        title={t('status_page_title')}
        subtitle={t('status_page_subtitle')}
        icon={ICONS.PULSE}
        headerType="list"
        documentTitle={t('status_page_title')}
      />

      {/* ── Row 1: What's Happening — Status + Changelog ──────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

      {/* System Status */}
      <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className={cn('absolute inset-x-0 top-0 h-1', overallStatusConfig.bgColor)} />
        <div className="p-5 sm:p-8">
          <div className="flex items-start gap-2 mb-4">
            <Icon
              name={overallStatusConfig.icon}
              size="md"
              color={overallStatusConfig.color}
              ariaHidden
            />
            <div>
              <h2 className="text-base sm:text-xl font-bold text-gray-900">{t('status_system_status')}</h2>
              {systemStatus && (
                <p className={cn('text-sm font-medium', overallStatusConfig.color)}>
                  {overallStatusConfig.label}
                </p>
              )}
            </div>
          </div>

          {/* Loading State */}
          {statusLoading && (
            <div className="flex items-center justify-center py-8">
              <Icon name={ICONS.LOADER} size="lg" color="text-gray-400" ariaHidden />
              <span className="ml-2 text-gray-600">{t('status_loading')}</span>
            </div>
          )}

          {/* Services Grid */}
          {systemStatus && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
                {systemStatus.services.map((service, index) => {
                  const serviceConfig = STATUS_CONFIG[service.effectiveStatus];
                  return (
                    <div
                      key={index}
                      className={cn(
                        'lg:aspect-square flex flex-col p-3 rounded-xl border-2 transition-all',
                        service.effectiveStatus === 'operational'
                          ? 'border-green-200 bg-green-50'
                          : service.effectiveStatus === 'degraded'
                            ? 'border-amber-200 bg-amber-50'
                            : service.effectiveStatus === 'down'
                              ? 'border-red-200 bg-red-50'
                              : 'border-primary-200 bg-primary-50'
                      )}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Icon
                          name={serviceConfig.icon}
                          size="sm"
                          color={serviceConfig.color}
                          ariaHidden
                        />
                        <span className="text-sm font-semibold text-gray-900 leading-tight">{service.name}</span>
                      </div>
                      <p className={cn('text-xs font-medium mt-auto', serviceConfig.color)}>
                        {serviceConfig.label}
                      </p>
                      {service.statusMessage && (
                        <p className="text-xs text-gray-600 mt-1">{service.statusMessage}</p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Active Incidents */}
              {systemStatus.activeIncidents.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('status_active_incidents')}</h3>
                  <div className="space-y-2">
                    {systemStatus.activeIncidents.map((incident) => (
                      <div
                        key={incident.id}
                        className="p-4 bg-amber-50 border border-amber-200 rounded-lg"
                      >
                        <div className="flex items-start gap-2">
                          <Icon name={ICONS.WARNING} size="sm" color="text-amber-600" ariaHidden />
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900">{incident.title}</p>
                            <p className="text-sm text-gray-700">{incident.message}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {t('status_line', { status: incident.status, date: formatDateTime(incident.updatedAt) })}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Scheduled Maintenance */}
              {systemStatus.scheduledMaintenance.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {t('status_scheduled_maintenance')}
                  </h3>
                  <div className="space-y-2">
                    {systemStatus.scheduledMaintenance.map((maint) => (
                      <div
                        key={maint.id}
                        className="p-4 bg-soft-mint border border-light-teal-gray rounded-lg"
                      >
                        <div className="flex items-start gap-2">
                          <Icon name={ICONS.TOOLS} size="sm" color="text-dark-teal" ariaHidden />
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900">{maint.title}</p>
                            {maint.message && <p className="text-sm text-gray-700">{maint.message}</p>}
                            <p className="text-xs text-gray-500 mt-1">
                              {formatDateTime(maint.scheduledStart)} - {formatDateTime(maint.scheduledEnd)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

        {/* Changelog */}
        <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-mint-h" />
          <div className="p-5 sm:p-8">
            <div className="flex items-start gap-2 mb-4">
              <Icon name={ICONS.FILE} size="md" color="text-dark-teal" ariaHidden />
              <h2 className="text-base sm:text-xl font-bold text-gray-900">{t('changelog_title')}</h2>
            </div>

            {changelogLoading && (
              <div className="flex items-center justify-center py-8">
                <Icon name={ICONS.LOADER} size="lg" color="text-gray-400" ariaHidden />
                <span className="ml-2 text-gray-600">{t('status_loading')}</span>
              </div>
            )}

            {changelog && changelog.entries.length > 0 && (
              <>
                <div>
                  {changelog.entries.slice(0, visibleCount).map((entry) => {
                    const typeConfig = CHANGELOG_TYPE_CONFIG[entry.type] || CHANGELOG_TYPE_CONFIG.feature;
                    return (
                      <button
                        key={entry.id}
                        type="button"
                        onClick={() => setSelectedChangelog(entry)}
                        className="group relative w-full text-left block border-l-2 border-gray-200 pl-6 pb-5 hover:border-gray-300 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className={cn(
                              'inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium',
                              typeConfig.color,
                              typeConfig.bgColor
                            )}
                          >
                            {typeConfig.label}
                          </span>
                          <span className="ml-auto text-xs text-gray-400 whitespace-nowrap">
                            {formatDateOnly(entry.publishedAt)}
                          </span>
                        </div>
                        <p className="relative text-sm font-semibold text-gray-900 group-hover:text-primary-600 transition-colors mt-1">
                          <span
                            className={cn(
                              'absolute -left-[29px] top-0 bottom-0 my-auto h-2.5 w-2.5 rounded-full ring-4 ring-white transition-transform group-hover:scale-110',
                              typeConfig.dotColor
                            )}
                          />
                          {entry.title}
                        </p>
                        {entry.description && (
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{entry.description}</p>
                        )}
                      </button>
                    );
                  })}
                </div>

                {changelog.entries.length > CHANGELOG_STEP && (
                  <button
                    type="button"
                    onClick={() =>
                      visibleCount < changelog.entries.length
                        ? setVisibleCount((c) => Math.min(c + CHANGELOG_STEP, changelog.entries.length))
                        : setVisibleCount(CHANGELOG_STEP)
                    }
                    className="w-full mt-1 py-2 text-xs font-medium text-gray-500 hover:text-primary-600 transition-colors text-center"
                  >
                    {visibleCount < changelog.entries.length
                      ? t('changelog_show_more')
                      : t('changelog_show_less')}
                  </button>
                )}
              </>
            )}

            {changelog && changelog.entries.length === 0 && (
              <p className="text-center text-gray-500 py-8">{t('changelog_no_updates')}</p>
            )}
          </div>
        </div>

      </div>{/* End Row 1 */}

      {/* ── Row 2: Your Feedback — Request Form + Previous Requests ────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Feature Request Form */}
        <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-mint-h" />
          <div className="p-5 sm:p-8">
            <div className="flex items-start gap-2 mb-4">
              <Icon name={ICONS.BULB} size="md" color="text-dark-teal" ariaHidden />
              <div>
                <h2 className="text-base sm:text-xl font-bold text-gray-900">{t('feedback_title')}</h2>
                <p className="text-sm text-gray-600">
                  {t('feedback_subtitle')}
                </p>
              </div>
            </div>

            {isAuthenticated ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Type Toggle Pills */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('feedback_type')}</label>
                  <div className="flex flex-wrap gap-2">
                    {REQUEST_TYPE_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, type: option.value as FeatureRequestType })}
                        className={cn(
                          'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200',
                          formData.type === option.value
                            ? 'bg-gray-900 text-white border border-gray-900'
                            : 'border border-gray-200 text-gray-600 bg-white hover:bg-gray-50 hover:border-gray-300'
                        )}
                      >
                        <Icon name={option.icon} size="xs" ariaHidden />
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Title Input */}
                <Input
                  label={t('feedback_title_label')}
                  placeholder={t('feedback_title_placeholder')}
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  error={errors.title}
                  maxLength={200}
                />

                {/* Description Textarea */}
                <Textarea
                  label={t('feedback_description')}
                  placeholder={t('feedback_description_placeholder')}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  error={errors.description}
                  maxLength={2000}
                  rows={4}
                  showCharCount
                />

                {/* Screenshot Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('feedback_screenshot')}
                  </label>
                  <div className="flex items-center gap-3">
                    <label className="relative cursor-pointer px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                      <span className="text-sm text-gray-700">
                        {screenshot ? screenshot.name : t('choose_file')}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="absolute inset-0 opacity-0 cursor-pointer"
                      />
                    </label>
                    {screenshot && (
                      <button
                        type="button"
                        onClick={() => setScreenshot(null)}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        {t('remove')}
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{t('feedback_screenshot_hint')}</p>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={submitMutation.isPending}
                  className={cn(
                    'w-full px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base rounded-lg sm:rounded-xl font-semibold text-white transition-all',
                    'bg-primary-400 hover:bg-primary-600',
                    'hover:shadow-lg',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  {submitMutation.isPending ? t('feedback_submitting') : t('feedback_submit')}
                </button>
              </form>
            ) : (
              <div className="text-center py-8">
                <Icon name={ICONS.LOGIN} size="xl" color="text-gray-400" ariaHidden />
                <p className="mt-4 text-gray-600">{t('feedback_login_required')}</p>
                <a
                  href="/login"
                  className="inline-block mt-4 px-6 py-2 bg-primary-600 text-primary-600-contrast rounded-lg hover:bg-primary-700 transition-colors"
                >
                  {t('login')}
                </a>
              </div>
            )}
          </div>
        </div>

        {/* User's Previous Requests */}
        {isAuthenticated && userRequests && userRequests.length > 0 ? (
        <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-mint-h" />
          <div className="p-5 sm:p-8">
            <div className="flex items-start gap-2 mb-4">
              <Icon name={ICONS.LIST} size="md" color="text-green-600" ariaHidden />
              <h2 className="text-base sm:text-xl font-bold text-gray-900">{t('feedback_your_requests')}</h2>
            </div>

            <div className="space-y-3">
              {userRequests.map((request) => {
                const statusConfig = STATUS_BADGE_CONFIG[request.status];
                const typeConfig = CHANGELOG_TYPE_CONFIG[request.type] || CHANGELOG_TYPE_CONFIG.feature;
                return (
                  <button
                    key={request.id}
                    type="button"
                    onClick={() => setSelectedRequest(request)}
                    className="w-full text-left flex items-start gap-3 p-4 rounded-lg border border-gray-200 hover:border-gray-300 cursor-pointer transition-colors"
                  >
                    <div className="flex-shrink-0 mt-1">
                      <span
                        className={cn(
                          'inline-flex items-center px-2 py-1 rounded-md text-xs font-medium',
                          typeConfig.color,
                          typeConfig.bgColor
                        )}
                      >
                        {typeConfig.label}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">{request.title}</p>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{request.description}</p>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <span
                        className={cn(
                          'inline-flex items-center px-2 py-1 rounded-md text-xs font-medium',
                          statusConfig.color,
                          statusConfig.bgColor
                        )}
                      >
                        {statusConfig.label}
                      </span>
                      <p className="text-xs text-gray-500 mt-1">{formatDateOnly(request.createdAt)}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        ) : (
          /* Empty state placeholder when no requests — keeps grid balanced */
          <div className="hidden lg:block" />
        )}

      </div>{/* End Row 2 */}

      {/* Changelog Detail Modal */}
      <Modal
        open={!!selectedChangelog}
        onOpenChange={(open) => !open && setSelectedChangelog(null)}
        title={selectedChangelog?.title || ''}
        size="md"
      >
        {selectedChangelog && (() => {
          const typeConfig = CHANGELOG_TYPE_CONFIG[selectedChangelog.type] || CHANGELOG_TYPE_CONFIG.feature;
          return (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    'inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium',
                    typeConfig.color,
                    typeConfig.bgColor
                  )}
                >
                  {typeConfig.label}
                </span>
                {selectedChangelog.version && (
                  <span className="text-xs font-medium text-gray-400">v{selectedChangelog.version}</span>
                )}
                <span className="ml-auto text-xs text-gray-400 whitespace-nowrap">
                  {formatDateOnly(selectedChangelog.publishedAt)}
                </span>
              </div>
              <div className="h-px bg-gray-100" />
              <p className="text-sm leading-relaxed text-gray-700 whitespace-pre-wrap">
                {selectedChangelog.description || t('no_description')}
              </p>
            </div>
          );
        })()}
      </Modal>

      {/* Feature Request Detail Modal */}
      <Modal
        open={!!selectedRequest}
        onOpenChange={(open) => !open && setSelectedRequest(null)}
        title={selectedRequest?.title || ''}
        size="md"
      >
        {selectedRequest && (() => {
          const typeConfig = CHANGELOG_TYPE_CONFIG[selectedRequest.type] || CHANGELOG_TYPE_CONFIG.feature;
          const statusConfig = STATUS_BADGE_CONFIG[selectedRequest.status];
          return (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    'inline-flex items-center px-2 py-1 rounded-md text-xs font-medium',
                    typeConfig.color,
                    typeConfig.bgColor
                  )}
                >
                  {typeConfig.label}
                </span>
                <span
                  className={cn(
                    'inline-flex items-center px-2 py-1 rounded-md text-xs font-medium',
                    statusConfig.color,
                    statusConfig.bgColor
                  )}
                >
                  {statusConfig.label}
                </span>
              </div>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedRequest.description}</p>
              {selectedRequest.screenshot && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">{t('feedback_screenshot')}</p>
                  <img
                    src={selectedRequest.screenshot}
                    alt=""
                    className="rounded-lg border border-gray-200 max-h-64 object-contain"
                  />
                </div>
              )}
              <div className="flex items-center justify-between text-xs text-gray-400 pt-3 border-t border-gray-100">
                <span>{t('submitted')}: {formatDateOnly(selectedRequest.createdAt)}</span>
                <span>{t('updated')}: {formatDateOnly(selectedRequest.updatedAt)}</span>
              </div>
            </div>
          );
        })()}
      </Modal>

    </div>
  );
};
